import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createFamily, sendInviteEmail } from '@coparent/shared/src/services/familyService';
import { addChild } from '@coparent/shared/src/services/childService';
import { createCustodySchedule } from '@coparent/shared/src/services/custodyService';
import { CUSTODY_PATTERNS } from '@coparent/shared/src/data/defaultPatterns';
import { track } from '@coparent/shared/src/services/analyticsService';
import { HiArrowRight, HiArrowLeft, HiCheck } from 'react-icons/hi';
import toast from 'react-hot-toast';

// Step instrumentation: each step is recorded only after its write succeeds, so
// the funnel measures completion rather than mere arrival. Previously only the
// outcome was visible (funnelJoinedFamily) and wizard drop-off was not.
const trackStep = (step) => track('onboarding_step_completed', { step, platform: 'web' });

const SetupWizard = () => {
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Fires once per mount — the denominator every later step is measured against.
  React.useEffect(() => {
    track('onboarding_started', { platform: 'web' });
  }, []);

  const [familyName, setFamilyName] = useState('');
  const [childName, setChildName] = useState('');
  const [childDob, setChildDob] = useState('');
  const [childSchool, setChildSchool] = useState('');
  const [selectedPattern, setSelectedPattern] = useState('2-2-3');
  const [scheduleStartDate, setScheduleStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [coParentEmail, setCoParentEmail] = useState('');
  const [createdFamily, setCreatedFamily] = useState(null);

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      toast.error('Please enter a family name');
      return;
    }
    setLoading(true);
    try {
      const family = await createFamily(
        currentUser.uid,
        familyName,
        currentUser.displayName
      );
      setCreatedFamily(family);
      trackStep('family');
      setStep(2);
    } catch (error) {
      toast.error('Failed to create family');
    }
    setLoading(false);
  };

  const handleAddChild = async () => {
    if (!childName.trim()) {
      toast.error('Please enter your child\'s name');
      return;
    }
    setLoading(true);
    try {
      await addChild(createdFamily.id, {
        firstName: childName,
        dateOfBirth: childDob || null,
        school: childSchool || null,
        grade: null,
        medicalNotes: null,
        emergencyContacts: [],
        photoURL: null
      });
      trackStep('child');
      setStep(3);
    } catch (error) {
      toast.error('Failed to add child');
    }
    setLoading(false);
  };

  const handleSetSchedule = async () => {
    setLoading(true);
    try {
      const pattern = CUSTODY_PATTERNS.find(p => p.id === selectedPattern);
      await createCustodySchedule(createdFamily.id, {
        type: 'recurring',
        pattern: selectedPattern,
        startDate: new Date(scheduleStartDate + 'T00:00:00'),
        endDate: null,
        cycleLength: pattern.cycleLength,
        cycleDays: pattern.cycleDays,
        createdBy: currentUser.uid
      });
      trackStep('schedule');
      setStep(4);
    } catch (error) {
      toast.error('Failed to set schedule');
    }
    setLoading(false);
  };

  const handleInvite = async () => {
    setLoading(true);
    try {
      if (coParentEmail) {
        await sendInviteEmail(createdFamily.id, coParentEmail, createdFamily.inviteCode);
        track('onboarding_invite_sent', { platform: 'web' });
      }
      trackStep('invite');
      track('onboarding_completed', {
        platform: 'web',
        invited: coParentEmail ? 'yes' : 'no',
      });
      await refreshUserData();
      toast.success('Setup complete!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to send invite');
    }
    setLoading(false);
  };

  const handleSkipInvite = async () => {
    // Tracked separately from a completed invite: skipping here is the moment a
    // family stays single-parent, which is the strongest predictor of a quiet
    // account (no reactive notification has anyone to fire for).
    track('onboarding_invite_skipped', { platform: 'web' });
    track('onboarding_completed', { platform: 'web', invited: 'no' });
    await refreshUserData();
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
      <div className="max-w-lg w-full animate-slide-up">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                s <= step ? 'bg-ink-800 w-12' : 'bg-surface-200 w-8'
              }`}
            />
          ))}
        </div>

        <div className="card p-8">
          {/* Step 1: Family Name */}
          {step === 1 && (
            <>
              <h2 className="text-2xl font-bold text-ink-900 tracking-tight mb-1.5">Create Your Family</h2>
              <p className="text-ink-400 text-sm mb-6">Give your co-parenting family a name.</p>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Family Name</label>
                <input
                  value={familyName}
                  onChange={e => setFamilyName(e.target.value)}
                  type="text"
                  className="input-base"
                  placeholder="e.g., The Cohen Family"
                />
              </div>
              <button
                onClick={handleCreateFamily}
                disabled={loading}
                className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
              >
                {loading ? 'Creating...' : 'Continue'}
                <HiArrowRight className="text-sm" />
              </button>
            </>
          )}

          {/* Step 2: Add Child */}
          {step === 2 && (
            <>
              <h2 className="text-2xl font-bold text-ink-900 tracking-tight mb-1.5">Add Your Child</h2>
              <p className="text-ink-400 text-sm mb-6">Tell us about your child.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Child's Name</label>
                  <input
                    value={childName}
                    onChange={e => setChildName(e.target.value)}
                    type="text"
                    className="input-base"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Date of Birth (optional)</label>
                  <input
                    value={childDob}
                    onChange={e => setChildDob(e.target.value)}
                    type="date"
                    className="input-base"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">School (optional)</label>
                  <input
                    value={childSchool}
                    onChange={e => setChildSchool(e.target.value)}
                    type="text"
                    className="input-base"
                    placeholder="School name"
                  />
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="btn-secondary flex items-center gap-1.5"
                >
                  <HiArrowLeft className="text-sm" /> Back
                </button>
                <button
                  onClick={handleAddChild}
                  disabled={loading}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {loading ? 'Adding...' : 'Continue'}
                  <HiArrowRight className="text-sm" />
                </button>
              </div>
            </>
          )}

          {/* Step 3: Custody Schedule */}
          {step === 3 && (
            <>
              <h2 className="text-2xl font-bold text-ink-900 tracking-tight mb-1.5">Custody Schedule</h2>
              <p className="text-ink-400 text-sm mb-6">Choose your custody rotation pattern.</p>
              <div className="space-y-2.5">
                {CUSTODY_PATTERNS.filter(p => p.popular).map(pattern => (
                  <button
                    key={pattern.id}
                    onClick={() => setSelectedPattern(pattern.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      selectedPattern === pattern.id
                        ? 'border-ink-800 bg-surface-50 shadow-warm-sm'
                        : 'border-surface-200 hover:border-surface-300'
                    }`}
                  >
                    <div className="font-semibold text-ink-900 text-sm">{pattern.name}</div>
                    <div className="text-xs text-ink-400 mt-0.5">{pattern.description}</div>
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Schedule Start Date</label>
                <input
                  value={scheduleStartDate}
                  onChange={e => setScheduleStartDate(e.target.value)}
                  type="date"
                  className="input-base"
                />
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="btn-secondary flex items-center gap-1.5"
                >
                  <HiArrowLeft className="text-sm" /> Back
                </button>
                <button
                  onClick={handleSetSchedule}
                  disabled={loading}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {loading ? 'Saving...' : 'Continue'}
                  <HiArrowRight className="text-sm" />
                </button>
              </div>
            </>
          )}

          {/* Step 4: Invite Co-Parent */}
          {step === 4 && (
            <>
              <h2 className="text-2xl font-bold text-ink-900 tracking-tight mb-1.5">Invite Co-Parent</h2>
              <p className="text-ink-400 text-sm mb-6">
                Share this invite code with your co-parent, or enter their email.
              </p>
              <div className="bg-surface-50 rounded-2xl p-5 text-center mb-6 border border-surface-200">
                <p className="text-xs text-ink-400 font-semibold uppercase tracking-wide mb-2">Invite Code</p>
                <p className="text-3xl font-mono font-bold text-accent-600 tracking-[0.2em]">
                  {createdFamily?.inviteCode}
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">
                  Co-Parent's Email (optional)
                </label>
                <input
                  value={coParentEmail}
                  onChange={e => setCoParentEmail(e.target.value)}
                  type="email"
                  className="input-base"
                  placeholder="their-email@example.com"
                />
              </div>
              <div className="mt-6 space-y-3">
                <button
                  onClick={handleInvite}
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <HiCheck className="text-base" />
                  {loading ? 'Finishing...' : 'Finish Setup'}
                </button>
                <button
                  onClick={handleSkipInvite}
                  className="w-full py-2.5 text-ink-400 hover:text-ink-700 transition-colors text-sm font-medium"
                >
                  Skip for now — I'll invite them later
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
